package er.prateek.crossly.generation

import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.data.model.DifficultyTier

/** One run of white cells that a single word has to fill. */
data class Slot(
    val row: Int,
    val col: Int,
    val direction: ClueDirection,
    val length: Int,
) {
    /** Grid index of the i-th cell of this slot, given a row stride of [width]. */
    fun cellIndex(i: Int, width: Int): Int =
        if (direction == ClueDirection.ACROSS) row * width + col + i else (row + i) * width + col
}

/**
 * A blank grid shape to be filled. [mask] uses the same `.`/`#` alphabet as the
 * authored packs, so a generated puzzle is indistinguishable from a hand-made
 * one downstream.
 */
data class GridTemplate(
    val id: String,
    val mask: List<String>,
) {
    val height: Int = mask.size
    val width: Int = mask.firstOrNull()?.length ?: 0

    fun isBlack(row: Int, col: Int): Boolean = mask[row][col] == '#'

    /**
     * Every maximal run of at least [MIN_SLOT_LENGTH] white cells.
     *
     * The floor is 3, not 2, because the word bank starts at 3 letters: a
     * template containing a 2-cell run is not merely awkward, it is silently
     * unfillable -- the slot has zero candidates, so every seed fails and the
     * tier generates nothing. Shorter runs are treated as a template design
     * error and rejected by [unfillableSlots].
     */
    val slots: List<Slot> by lazy {
        buildList {
            for (row in 0 until height) {
                var col = 0
                while (col < width) {
                    if (isBlack(row, col)) { col++; continue }
                    var end = col
                    while (end < width && !isBlack(row, end)) end++
                    if (end - col >= MIN_SLOT_LENGTH) {
                        add(Slot(row, col, ClueDirection.ACROSS, end - col))
                    }
                    col = end
                }
            }
            for (col in 0 until width) {
                var row = 0
                while (row < height) {
                    if (isBlack(row, col)) { row++; continue }
                    var end = row
                    while (end < height && !isBlack(end, col)) end++
                    if (end - row >= MIN_SLOT_LENGTH) {
                        add(Slot(row, col, ClueDirection.DOWN, end - row))
                    }
                    row = end
                }
            }
        }
    }

    /**
     * White cells that no slot covers, plus any white run too short to be a
     * word. Such a cell has no solution letter behind it, so the puzzle can
     * never be completed. Empty means the shape is safe to generate into.
     */
    fun unfillableSlots(): List<Pair<Int, Int>> {
        val covered = buildSet {
            for (slot in slots) {
                for (i in 0 until slot.length) {
                    val index = slot.cellIndex(i, width)
                    add(index / width to index % width)
                }
            }
        }
        return buildList {
            for (row in 0 until height) {
                for (col in 0 until width) {
                    if (!isBlack(row, col) && (row to col) !in covered) add(row to col)
                }
            }
        }
    }

    /**
     * Standard crossword numbering: scan row-major, and number a cell the first
     * time it begins an across or a down word. Matches the authored packs.
     */
    val numberByCell: Map<Int, Int> by lazy {
        val starts = slots.map { it.row * width + it.col }.toSet()
        var next = 1
        buildMap {
            for (index in 0 until width * height) {
                if (index in starts) put(index, next++)
            }
        }
    }
}

/**
 * Grid shapes per tier, chosen to match the dimensions the existing UI and
 * authored packs already use: 3x3 and 4x4 fully open, 5x5 with cut corners.
 */
/** Shortest word the bundled bank can supply; see [GridTemplate.slots]. */
const val MIN_SLOT_LENGTH = 3

object GridTemplates {
    private val open3 = GridTemplate("open-3x3", listOf("...", "...", "..."))
    private val open4 = GridTemplate("open-4x4", listOf("....", "....", "....", "...."))
    private val cut5 = GridTemplate(
        "cut-5x5",
        listOf("#...#", ".....", ".....", ".....", "#...#"),
    )
    private val corner5 = GridTemplate(
        "corner-5x5",
        listOf("#....", ".....", ".....", ".....", "....#"),
    )

    /**
     * A fully open 5x5 is deliberately absent. It needs ten mutually consistent
     * 5-letter words -- a genuine double word square of order 5 -- which a
     * ~1.8k-word pool almost never admits: measured, it filled 3 seeds in 20
     * and took ~8s to fail the rest. [corner5] keeps two extra long crossings
     * over [cut5] while still filling on every seed tried.
     */
    fun forTier(tier: DifficultyTier): List<GridTemplate> = when (tier) {
        DifficultyTier.EASY -> listOf(open3)
        DifficultyTier.MEDIUM -> listOf(open4)
        DifficultyTier.HARD -> listOf(cut5)
        DifficultyTier.EXPERT -> listOf(cut5, corner5)
    }

    /** Every shape the app can generate, for validation. */
    val all: List<GridTemplate> = listOf(open3, open4, cut5, corner5)
}
